// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import styles from '../Backstage.module.css'
import type { ChatRow } from '../utils/types'
import { useReadOnlyMode } from '../../../store/hooks/useReadOnlyMode'
import { useAppDispatch, useAppSelector } from '../../../store/hooks/hooks'
import { setNodes } from '../../../store/slices/selectionSlice'
import { ChatBubble } from '../chat/ChatBubble'
import { isToolNote } from '../utils/toolNames'
import { ChatInputArea } from '../chat/ChatInputArea'
import { RecommendationBubbles } from '../chat/RecommendationBubbles'
import type { ContextUsage } from '../../../store/slices/chatSlice'
import { selectActiveSessionId } from '../../../store/selectors/sessionSelectors'
import { selectIsActiveChatProcessing } from '../../../store/selectors/chatSelectors'
import { useAuth } from '../../../store/hooks/useAuth'
import { getUserProfile } from '../../../services/user'
import { CreditRechargeModal } from '../components/CreditRechargeModal'
import { LowCreditBanner } from '../components/LowCreditBanner'
import type { AgentSuggestion } from '../../../services/agent'

export interface ChatPageProps {
    sessionActive: boolean
    messages: ChatRow[]
    input: string
    contextUsage: ContextUsage | null
    bottomRef: React.RefObject<HTMLDivElement | null>
    messagesContainerRef: React.RefObject<HTMLDivElement | null>
    fileInputRef: React.RefObject<HTMLInputElement | null>
    textareaRef: React.RefObject<HTMLTextAreaElement | null>
    canSend: boolean
    onPickFile: () => void
    onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    onMessagesScroll: () => void
    onInputChange: (value: string) => void
    onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
    onCompositionStart: () => void
    onCompositionEnd: () => void
    onSend: () => void
    onSendPrompt: (prompt: string) => void
    recommendations: AgentSuggestion[]
    onRefreshRecommendations: () => void
    isManualRefreshingRecommendations: boolean
}

export function ChatPage(props: ChatPageProps): ReactElement {
    const {
        sessionActive,
        messages,
        input,
        contextUsage,
        bottomRef,
        messagesContainerRef,
        fileInputRef,
        textareaRef,
        canSend,
        onPickFile,
        onFileChange,
        onMessagesScroll,
        onInputChange,
        onKeyDown,
        onCompositionStart,
        onCompositionEnd,
        onSend,
        onSendPrompt,
        recommendations,
        onRefreshRecommendations,
        isManualRefreshingRecommendations,
    } = props

    const readOnly = useReadOnlyMode()
    const dispatch = useAppDispatch()
    const { user } = useAuth()
    const activeSessionId = useAppSelector(selectActiveSessionId)
    const isProcessing = useAppSelector(selectIsActiveChatProcessing)

    const [creditBalance, setCreditBalance] = useState<number | null>(null)
    const [isRechargeOpen, setIsRechargeOpen] = useState(false)

    const LOW_CREDIT_THRESHOLD = 500
    const shouldShowCreditBanner =
        typeof creditBalance === 'number' &&
        Number.isFinite(creditBalance) &&
        creditBalance < LOW_CREDIT_THRESHOLD

    const refreshCreditBalance = useCallback(async () => {
        if (readOnly || !user) {
            setCreditBalance(null)
            return
        }
        try {
            const profile = await getUserProfile()
            if (typeof profile.credit_balance === 'number') {
                setCreditBalance(profile.credit_balance)
            } else {
                setCreditBalance(null)
            }
        } catch {
            // Best-effort: don't block chat UX if profile fetch fails.
        }
    }, [readOnly, user])

    const prevIsProcessing = useRef(isProcessing)
    useEffect(() => {
        if (!prevIsProcessing.current && isProcessing) {
            prevIsProcessing.current = isProcessing
            return
        }
        if (prevIsProcessing.current && !isProcessing) {
            void refreshCreditBalance()
        }
        prevIsProcessing.current = isProcessing
    }, [isProcessing, refreshCreditBalance])

    useEffect(() => {
        void refreshCreditBalance()
    }, [refreshCreditBalance])

    useEffect(() => {
        if (readOnly || !user) return

        const handleFocus = () => {
            void refreshCreditBalance()
        }
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void refreshCreditBalance()
            }
        }
        const handleOpenRecharge = () => {
            setIsRechargeOpen(true)
        }

        window.addEventListener('focus', handleFocus)
        window.addEventListener('open:recharge', handleOpenRecharge)
        document.addEventListener('visibilitychange', handleVisibilityChange)
        return () => {
            window.removeEventListener('focus', handleFocus)
            window.removeEventListener('open:recharge', handleOpenRecharge)
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [readOnly, refreshCreditBalance, user])

    const handleMessageClick = (nodeIds: string[]) => {
        if (!activeSessionId) return
        if (nodeIds.length > 0) {
            dispatch(setNodes({ sessionId: activeSessionId, nodeIds }))
            const focusNodeId = nodeIds[nodeIds.length - 1]
            window.dispatchEvent(
                new CustomEvent('canvas:focus-node', {
                    detail: { nodeId: focusNodeId, sessionId: activeSessionId },
                })
            )
        }
    }

    const activeToolNoteId = useMemo(() => {
        if (!isProcessing) return null
        for (let i = messages.length - 1; i >= 0; i -= 1) {
            const msg = messages[i]
            if (msg.kind === 'note' && isToolNote(msg.text)) {
                return msg.id
            }
        }
        return null
    }, [isProcessing, messages])

    // Collapse consecutive identical tool-progress steps (e.g. four retouch
    // passes for a four-angle shoot) into one line with a ×N count, so the
    // thread reads as directed work, not a repeating machine log.
    const chatRows = useMemo(() => {
        const rows: { message: ChatRow; count: number; ids: string[] }[] = []
        for (const m of messages) {
            const last = rows[rows.length - 1]
            if (
                m.kind === 'note' &&
                isToolNote(m.text) &&
                last &&
                last.message.kind === 'note' &&
                last.message.text === m.text
            ) {
                last.count += 1
                last.ids.push(m.id)
            } else {
                rows.push({ message: m, count: 1, ids: [m.id] })
            }
        }
        return rows
    }, [messages])

    return (
        <div className={styles.chatColumn}>
            <div className={styles.messagesStack}>
                <div
                    className={styles.messages}
                    ref={messagesContainerRef}
                    onScroll={onMessagesScroll}
                >
                    {chatRows.map((row) => (
                        <ChatBubble
                            key={row.message.id}
                            message={row.message}
                            count={row.count}
                            onMessageClick={handleMessageClick}
                            showToolLoader={Boolean(activeToolNoteId && row.ids.includes(activeToolNoteId))}
                        />
                    ))}
                    <div ref={bottomRef} />
                </div>
                <div className={styles.recommendationOverlay}>
                    <RecommendationBubbles
                        disabled={readOnly || !sessionActive || isProcessing}
                        onSelect={onSendPrompt}
                        recommendations={recommendations}
                        onRefresh={onRefreshRecommendations}
                        isRefreshing={isManualRefreshingRecommendations}
                    />
                </div>
            </div>
            {isProcessing && messages.length > 0 && !activeToolNoteId && (
                <div className={styles.backgroundProcessingBanner}>
                    Your request is still being processed...
                </div>
            )}
            {shouldShowCreditBanner && (
                <LowCreditBanner
                    onRecharge={() => setIsRechargeOpen(true)}
                />
            )}
            <ChatInputArea
                input={input}
                sessionActive={sessionActive}
                readOnly={readOnly}
                canSend={canSend}
                contextUsage={contextUsage}
                fileInputRef={fileInputRef}
                textareaRef={textareaRef}
                onPickFile={onPickFile}
                onFileChange={onFileChange}
                onInputChange={onInputChange}
                onKeyDown={onKeyDown}
                onCompositionStart={onCompositionStart}
                onCompositionEnd={onCompositionEnd}
                onSend={onSend}
            />
            <CreditRechargeModal
                isOpen={isRechargeOpen}
                onClose={() => setIsRechargeOpen(false)}
            />
        </div>
    )
}

export default ChatPage
